import React from 'react';
import { Box, Typography, Button, Card, CardContent, Avatar, useMediaQuery, useTheme } from '@mui/material';
import { Pets, PhotoCamera, Psychology } from '@mui/icons-material';

interface WelcomeStepProps {
  onStart: () => void;
  onCancel: () => void;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
}

export const WelcomeStep: React.FC<WelcomeStepProps> = ({
  onStart,
  onCancel,
  userLanguage
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // REQUIRED: Language detection
  const isChinese = userLanguage === 'zh-CN';
  const isSpanish = userLanguage === 'es';
  const isFrench = userLanguage === 'fr';

  // REQUIRED: Text objects for localization
  const englishTexts = {
    title: "Welcome to PetSnap! 🐾",
    subtitle: "Let's create a personalized profile for your furry friend",
    description: "Build a comprehensive pet profile by uploading a photo and gathering key details. This will enable personalized care recommendations and training plans.",
    smartPhotoAnalysis: "Pet Photo Upload",
    smartPhotoAnalysisDesc: "Upload a photo to personalize your pet's profile",
    personalizedRecommendations: "Personalized Recommendations",
    personalizedRecommendationsDesc: "Get tailored care advice based on your pet's unique profile",
    completePetProfile: "Complete Pet Profile",
    completePetProfileDesc: "Build a comprehensive profile for better pet care and planning",
    startButton: "Let's Get Started!",
    cancelButton: "Maybe Later"
  };

  const chineseTexts = {
    title: "欢迎使用PetSnap！🐾",
    subtitle: "让我们为您的毛茸茸朋友创建一个个性化资料",
    description: "通过上传照片并提供关键详情来为您的宠物创建全面的资料。这将实现个性化的护理建议和训练计划。",
    smartPhotoAnalysis: "上传宠物照片",
    smartPhotoAnalysisDesc: "上传照片以个性化您宠物的资料",
    personalizedRecommendations: "个性化推荐",
    personalizedRecommendationsDesc: "根据您宠物的独特资料获得量身定制的护理建议",
    completePetProfile: "完整宠物资料",
    completePetProfileDesc: "构建全面的资料以获得更好的宠物护理和规划",
    startButton: "让我们开始吧！",
    cancelButton: "稍后再说"
  };

  const spanishTexts = {
    title: "¡Bienvenido a PetSnap! 🐾",
    subtitle: "Creemos un perfil personalizado para tu amigo peludo",
    description: "Crea un perfil completo para tu mascota subiendo una foto y proporcionando detalles clave. Esto permitirá recomendaciones de cuidado personalizadas y planes de entrenamiento.",
    smartPhotoAnalysis: "Subir Foto de Mascota",
    smartPhotoAnalysisDesc: "Sube una foto para personalizar el perfil de tu mascota",
    personalizedRecommendations: "Recomendaciones Personalizadas",
    personalizedRecommendationsDesc: "Obtén consejos de cuidado personalizados basados en el perfil único de tu mascota",
    completePetProfile: "Perfil Completo de Mascota",
    completePetProfileDesc: "Construye un perfil completo para mejor cuidado y planificación de mascotas",
    startButton: "¡Empecemos!",
    cancelButton: "Tal vez más tarde"
  };

  const frenchTexts = {
    title: "Bienvenue sur PetSnap ! 🐾",
    subtitle: "Créons un profil personnalisé pour votre ami à fourrure",
    description: "Créez un profil complet pour votre animal en téléchargeant une photo et en fournissant des détails clés. Cela permettra des recommandations de soins personnalisées et des plans d'entraînement.",
    smartPhotoAnalysis: "Télécharger la Photo de l'Animal",
    smartPhotoAnalysisDesc: "Téléchargez une photo pour personnaliser le profil de votre animal",
    personalizedRecommendations: "Recommandations Personnalisées",
    personalizedRecommendationsDesc: "Obtenez des conseils de soins personnalisés basés sur le profil unique de votre animal",
    completePetProfile: "Profil Complet d'Animal",
    completePetProfileDesc: "Construisez un profil complet pour de meilleurs soins et planification d'animaux",
    startButton: "Commençons !",
    cancelButton: "Peut-être plus tard"
  };

  // REQUIRED: getText function for localization
  const getText = (key: string): string => {
    if (isChinese) return chineseTexts[key as keyof typeof chineseTexts];
    if (isSpanish) return spanishTexts[key as keyof typeof spanishTexts];
    if (isFrench) return frenchTexts[key as keyof typeof frenchTexts];
    return englishTexts[key as keyof typeof englishTexts];
  };


  return (
    <Box textAlign="center" py={isMobile ? 2 : 4}>
      {/* Welcome Header */}
      <Box mb={isMobile ? 3 : 4}>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          color="primary"
          sx={{ fontWeight: 600 }}
        >
          {getText('title')}
        </Typography>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          component="h2" 
          gutterBottom
          sx={{ px: isMobile ? 2 : 0 }}
        >
          {getText('subtitle')}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            maxWidth: isMobile ? '100%' : 600, 
            mx: 'auto', 
            mt: 2,
            px: isMobile ? 2 : 0,
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}
        >
          {getText('description')}
        </Typography>
      </Box>

      {/* Features Grid */}
      <Box sx={{ 
        mb: isMobile ? 3 : 4, 
        maxWidth: isMobile ? '100%' : 800, 
        mx: 'auto', 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? 2 : 3, 
        justifyContent: 'center',
        px: isMobile ? 1 : 0
      }}>
        {[
          {
            icon: <PhotoCamera fontSize="large" color="primary" />,
            title: getText('smartPhotoAnalysis'),
            description: getText('smartPhotoAnalysisDesc')
          },
          {
            icon: <Psychology fontSize="large" color="primary" />,
            title: getText('personalizedRecommendations'),
            description: getText('personalizedRecommendationsDesc')
          },
          {
            icon: <Pets fontSize="large" color="primary" />,
            title: getText('completePetProfile'),
            description: getText('completePetProfileDesc')
          }
        ].map((feature, index) => (
          <Box key={index} sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            maxWidth: isMobile ? '100%' : '400px', 
            minWidth: isMobile ? '280px' : '250px' 
          }}>
            <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ textAlign: 'center', flex: 1, p: isMobile ? 2 : 3 }}>
                <Box mb={2}>
                  <Avatar sx={{ 
                    mx: 'auto', 
                    bgcolor: 'primary.light', 
                    width: isMobile ? 56 : 64, 
                    height: isMobile ? 56 : 64 
                  }}>
                    {feature.icon}
                  </Avatar>
                </Box>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  component="h3" 
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {feature.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.85rem' : '0.875rem' }}
                >
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Action Buttons */}
      <Box 
        display="flex" 
        justifyContent="center" 
        gap={isMobile ? 1.5 : 2} 
        flexWrap="wrap"
        flexDirection={isMobile ? 'column' : 'row'}
        sx={{ px: isMobile ? 2 : 0 }}
      >
        <Button
          variant="contained"
          size={isMobile ? "medium" : "large"}
          onClick={onStart}
          fullWidth={isMobile}
          sx={{ 
            minWidth: isMobile ? '100%' : 200, 
            py: isMobile ? 1.5 : 1.5,
            minHeight: isMobile ? 44 : 'auto'
          }}
        >
          {getText('startButton')}
        </Button>
        <Button
          variant="outlined"
          size={isMobile ? "medium" : "large"}
          onClick={onCancel}
          fullWidth={isMobile}
          sx={{ 
            minWidth: isMobile ? '100%' : 150, 
            py: isMobile ? 1.5 : 1.5,
            minHeight: isMobile ? 44 : 'auto'
          }}
        >
          {getText('cancelButton')}
        </Button>
      </Box>
    </Box>
  );
};
